import React, { useState, useEffect, useRef } from "react";
import {
	Box,
	Paper,
	Typography,
	IconButton,
	Collapse,
	Fade,
	styled,
	useTheme,
	alpha,
} from "@mui/material";
import {
	ChevronLeft,
	ChevronRight,
	CalendarMonth,
	Check,
} from "@mui/icons-material";
import dayjs, { Dayjs } from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import weekOfYear from "dayjs/plugin/weekOfYear";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";

// Extend dayjs with plugins
dayjs.extend(isoWeek);
dayjs.extend(weekOfYear);
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

interface WeekSelectorProps {
	value: {
		startDate: Dayjs | null;
		endDate: Dayjs | null;
	};
	onChange: (value: { startDate: Dayjs | null; endDate: Dayjs | null }) => void;
	disableFuture?: boolean;
	disablePast?: boolean;
	maxDropdownHeight?: number | string;
}

interface StyledWeekRowProps {
	selected?: boolean;
	isOddWeek?: boolean;
}

const StyledWeekRow = styled(Paper, {
	shouldForwardProp: (prop) => prop !== "selected" && prop !== "isOddWeek",
})<StyledWeekRowProps>(({ theme, selected, isOddWeek }) => ({
	padding: theme.spacing(1),
	display: "flex",
	justifyContent: "space-between",
	alignItems: "center",
	margin: theme.spacing(0.5, 0),
	cursor: "pointer",
	transition: "all 0.2s ease-in-out",
	backgroundColor: selected
		? alpha(theme.palette.primary.main, 0.15)
		: "transparent",
	border: selected
		? `1px solid ${theme.palette.primary.main}`
		: "1px solid transparent",
	"&:hover": {
		backgroundColor: selected
			? alpha(theme.palette.primary.main, 0.2)
			: alpha(theme.palette.action.hover, 0.1),
		transform: "translateY(-2px)",
		boxShadow: selected ? theme.shadows[2] : "none",
	},
	position: "relative",
	...(isOddWeek && {
		borderLeft: `3px solid ${theme.palette.secondary.main}`,
	}),
}));

const CalendarHeader = styled(Box)(({ theme }) => ({
	display: "flex",
	justifyContent: "space-between",
	alignItems: "center",
	padding: theme.spacing(1, 0),
	marginBottom: theme.spacing(1),
	borderBottom: `1px solid ${theme.palette.divider}`,
	position: "sticky",
	top: 0,
	backgroundColor: theme.palette.background.paper,
	zIndex: 1,
}));

interface WeekNumberProps {
	isOddWeek?: boolean;
}

const WeekNumber = styled(Box)<WeekNumberProps>(({ theme, isOddWeek }) => ({
	position: "absolute",
	right: theme.spacing(1),
	top: theme.spacing(1),
	padding: theme.spacing(0.5, 1),
	borderRadius: theme.shape.borderRadius,
	backgroundColor: isOddWeek
		? theme.palette.secondary.main
		: theme.palette.primary.main,
	color: "#fff",
	fontSize: "0.7rem",
	fontWeight: "bold",
}));

const WeekRows = styled(Box)(({ theme, maxHeight }: any) => ({
	maxHeight: maxHeight || "320px",
	overflowY: "auto",
	scrollbarWidth: "thin",
	"&::-webkit-scrollbar": {
		width: "6px",
	},
	"&::-webkit-scrollbar-track": {
		background: alpha(theme.palette.common.black, 0.05),
		borderRadius: "10px",
	},
	"&::-webkit-scrollbar-thumb": {
		background: alpha(theme.palette.primary.main, 0.2),
		borderRadius: "10px",
		"&:hover": {
			background: alpha(theme.palette.primary.main, 0.4),
		},
	},
	padding: theme.spacing(0.5, 0.5),
}));

const WeekSelector: React.FC<WeekSelectorProps> = ({
	value,
	onChange,
	disableFuture = false,
	disablePast = false,
	maxDropdownHeight = "320px",
}) => {
	const theme = useTheme();
	const [open, setOpen] = useState(false);
	const [currentMonth, setCurrentMonth] = useState(dayjs());
	const selectorRef = useRef<HTMLDivElement>(null);
	const dropdownRef = useRef<HTMLDivElement>(null);
	const [selectedWeek, setSelectedWeek] = useState<{
		startDate: Dayjs | null;
		endDate: Dayjs | null;
		weekNumber: number | null;
	}>({
		startDate: value.startDate || null,
		endDate: value.endDate || null,
		weekNumber: value.startDate ? value.startDate.week() : null,
	});

	useEffect(() => {
		if (value.startDate) {
			setCurrentMonth(value.startDate);
			setSelectedWeek({
				startDate: value.startDate,
				endDate: value.endDate,
				weekNumber: value.startDate.week(),
			});
		}
	}, [value.startDate, value.endDate]);

	// Improved handling of outside clicks
	useEffect(() => {
		function handleClickOutside(event: MouseEvent) {
			// Only process if dropdown is open
			if (!open) return;

			// Check if click is outside both the selector and its dropdown
			if (
				selectorRef.current &&
				!selectorRef.current.contains(event.target as Node) &&
				dropdownRef.current &&
				!dropdownRef.current.contains(event.target as Node)
			) {
				setOpen(false);
			}
		}

		// Bind the event listener
		document.addEventListener("mousedown", handleClickOutside);
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, [open]); // Add open as a dependency to re-register when open state changes

	// Generate weeks for the current month view
	// Updated to handle different week types correctly:

	const getWeeksInMonth = () => {
		const firstDayOfMonth = currentMonth.startOf("month");
		const lastDayOfMonth = currentMonth.endOf("month");

		// Calculate the first Monday (for week start)
		let firstMonday = firstDayOfMonth.clone();
		// If the month doesn't start on Monday, find the next Monday
		if (firstMonday.day() !== 1) {
			// If it's Sunday (0), add 1 day; otherwise, add days until Monday
			firstMonday =
				firstMonday.day() === 0
					? firstMonday.add(1, "day")
					: firstMonday.add(8 - firstMonday.day(), "day");
		}

		// Start from the first Monday that affects this month
		// This could be in the previous month if the month starts mid-week
		let weekStart =
			firstDayOfMonth.day() === 1
				? firstDayOfMonth
				: firstDayOfMonth.day() === 0
				? firstDayOfMonth.add(1, "day") // If Sunday, start from Monday
				: firstDayOfMonth.subtract(firstDayOfMonth.day() - 1, "day");

		const weeks = [];
		let weekCounter = 1; // Start week counting at 1

		// Create weeks until we pass the end of the month
		while (weekStart.isSameOrBefore(lastDayOfMonth, "day")) {
			// Calculate end dates
			// Modified logic: Odd-numbered weeks (1, 3, 5) include Saturday, even weeks end on Friday
			const isOddWeek = weekCounter % 2 === 1;
			const weekEnd = isOddWeek
				? weekStart.clone().add(5, "day") // Saturday for odd weeks (1, 3, 5)
				: weekStart.clone().add(4, "day"); // Friday for even weeks (2, 4, 6)

			// Add the week object if it overlaps with the current month
			if (
				!(
					weekEnd.isBefore(firstDayOfMonth) || weekStart.isAfter(lastDayOfMonth)
				)
			) {
				const isInMonth =
					weekStart.month() === currentMonth.month() ||
					weekEnd.month() === currentMonth.month();

				if (isInMonth) {
					const weekObj = {
						startDate: weekStart.clone(),
						endDate: weekEnd.clone(),
						weekOfMonth: weekCounter,
						weekOfYear: weekStart.week(),
						isOddWeek: isOddWeek,
						includesSaturday: isOddWeek, // Odd weeks (1, 3, 5) end on Saturday, even on Friday
					};

					weeks.push(weekObj);
					weekCounter++; // Increment counter only when we add a week
				}
			}

			// Move to next Monday
			weekStart = weekStart.add(7, "day");
		}

		return weeks;
	};
	const handlePrevMonth = () => {
		setCurrentMonth(currentMonth.subtract(1, "month"));
	};

	const handleNextMonth = () => {
		setCurrentMonth(currentMonth.add(1, "month"));
	};

	const handleSelectWeek = (week: any) => {
		setSelectedWeek({
			startDate: week.startDate,
			endDate: week.endDate,
			weekNumber: week.weekOfYear,
		});
		onChange({ startDate: week.startDate, endDate: week.endDate });
		setOpen(false);
	};

	const isWeekDisabled = (week: any) => {
		const now = dayjs();
		if (disableFuture && week.startDate.isAfter(now)) {
			return true;
		}
		if (disablePast && week.endDate.isBefore(now, "day")) {
			return true;
		}
		return false;
	};

	const toggleCalendar = () => {
		setOpen(!open);
	};

	const weeks = getWeeksInMonth();

	return (
		<Box
			sx={{ width: "100%", maxWidth: 350, position: "relative" }}
			ref={selectorRef}
		>
			<Paper
				sx={{
					p: 1.5,
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					cursor: "pointer",
					border: open
						? `1px solid ${theme.palette.primary.main}`
						: "1px solid #e0e0e0",
					boxShadow: open ? 2 : 0,
					transition: "all 0.2s ease",
				}}
				onClick={toggleCalendar}
				elevation={0}
			>
				<Box>
					{selectedWeek.startDate ? (
						<Box>
							<Typography variant="body1" fontWeight="bold">
								{selectedWeek.startDate.format("MMM DD")} -{" "}
								{selectedWeek.endDate?.format("MMM DD, YYYY")}
							</Typography>
							<Typography variant="caption" color="textSecondary">
								Week {selectedWeek.weekNumber}{" "}
								{selectedWeek.startDate && (
									<>
										{weeks.find((w) =>
											w.startDate.isSame(selectedWeek.startDate, "day")
										)?.isOddWeek
											? "Mon-Sat"
											: "Mon-Fri"}
									</>
								)}
							</Typography>
						</Box>
					) : (
						<Typography variant="body1" color="textSecondary">
							Select a week
						</Typography>
					)}
				</Box>
				<IconButton
					size="small"
					onClick={(e) => {
						e.stopPropagation();
						toggleCalendar();
					}}
				>
					<CalendarMonth fontSize="small" color={open ? "primary" : "action"} />
				</IconButton>
			</Paper>

			<Collapse in={open}>
				<Paper
					ref={dropdownRef}
					sx={{
						position: "absolute",
						top: "calc(100% + 5px)",
						left: 0,
						width: "100%",
						zIndex: 1300,
						mt: 0.5,
						p: 2,
						boxShadow: 3,
					}}
				>
					<CalendarHeader>
						<IconButton size="small" onClick={handlePrevMonth}>
							<ChevronLeft />
						</IconButton>
						<Typography variant="subtitle1" fontWeight="bold">
							{currentMonth.format("MMMM YYYY")}
						</Typography>
						<IconButton size="small" onClick={handleNextMonth}>
							<ChevronRight />
						</IconButton>
					</CalendarHeader>

					<WeekRows maxHeight={maxDropdownHeight}>
						{weeks.map((week, index) => {
							const isDisabled = isWeekDisabled(week);
							const isSelected =
								selectedWeek.startDate?.isSame(week.startDate, "day") || false;

							return (
								<Fade in={true} key={index} timeout={100 + index * 50}>
									<div>
										<StyledWeekRow
											selected={isSelected}
											isOddWeek={week.isOddWeek}
											onClick={() => !isDisabled && handleSelectWeek(week)}
											sx={{
												opacity: isDisabled ? 0.5 : 1,
												pointerEvents: isDisabled ? "none" : "auto",
											}}
										>
											<Box>
												<Typography variant="body2">
													{week.startDate.format("MMM DD")} -{" "}
													{week.endDate.format("MMM DD")}
												</Typography>
												<Typography variant="caption" color="textSecondary">
													{week.isOddWeek
														? "Monday - Saturday"
														: "Monday - Friday"}
												</Typography>
											</Box>
											{isSelected && (
												<IconButton size="small" color="primary">
													<Check fontSize="small" />
												</IconButton>
											)}

											<WeekNumber isOddWeek={week.isOddWeek}>
												W{week.weekOfMonth}
											</WeekNumber>
										</StyledWeekRow>
									</div>
								</Fade>
							);
						})}
					</WeekRows>
				</Paper>
			</Collapse>
		</Box>
	);
};

export default WeekSelector;
